import { Box, Button, Modal, TextField, Tooltip, Typography } from "@mui/material";
import { getProjectLastLabelSeq, getProject, updateDatasetFile, createProject } from "../../services/projectService";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import { projectConfigState, projectImagesInfoState, projectInfoState, selectedImageState } from "../../recoil";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { formatDateTime, formatNumber, getUniqueProjectName } from "../../utils/utils";
import { getLatestInspResult } from "../../services/inspLogService";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ImageTable, { FilePathList } from "./ImageTable";
import InspProjectTable from "./InspProjectTable";
import { extractImgPaths, randomizeLabels } from "../../utils/datasetFileUtil";
import { IAddProject } from "@types";
import { initialProjectData } from "../../pages/ProjectPage";
import { GridColDef } from "@mui/x-data-grid";
import { getDatasetInfo, getFile } from "common-lib";
import { toast } from "react-toastify";
import { useTutorial } from "../../context/TutorialContext";
import { myAIModuleState, validatingStateAtom } from "../../recoil/upgrade";

interface ImageInfoItem {
  AILabel: string;
  ai_update_at: string;
  id: number;
  label: string;
  path: string;
  recentLabel: string;
  score: number;
  seq: number;
}

export default function ProjectLabelPart({ step, dataSetId }: { step: string; dataSetId: number }) {
  const { id } = useParams();
  const { label } = useParams();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useRecoilState(projectInfoState);
  const projectConfig = useRecoilValue(projectConfigState);
  const [projectImagesInfo, setProjectImagesInfo] = useRecoilState(projectImagesInfoState);
  const [selectedRow, setSelectedRow] = useRecoilState(selectedImageState);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const rowRef = useRef<Record<string, HTMLDivElement | null>>({});
  const tableRef = useRef<HTMLDivElement | null>(null);
  // project image 데이터 첫 로드 여부
  const [didInitialSelect, setDidInitialSelect] = useState(false);
  const [labelCount, setLabelCount] = useState<number>(4);
  const [openAnalysisModal, setOpenAnalysisModal] = useState<boolean>(false);
  const [isKeyHandling, setIsKeyHandling] = useState(false);
  const isLastClass = !!projectData?.class && projectData.class.indexOf(label ?? "") === projectData.class.length - 1;
  const myAIModule = useRecoilValue(myAIModuleState);
  const resetAIModule = useResetRecoilState(myAIModuleState);
  const { run, nextSubStep } = useTutorial();
  const [recentInspTime, setRecentInspTime] = useState<string | null>(null);
  const isValidating = !!useRecoilValue(validatingStateAtom)[Number(id)];

  useEffect(() => {
    if (!id || isValidating) return;
    getLatestInspResult(Number(id), undefined, "end")
      .then((res) => setRecentInspTime(res?.finished_at ?? null))
      .catch(() => setRecentInspTime(null));
  }, [id, step, isValidating]);

  const scrollToRow = (seq: number) => {
    const container = tableRef.current;
    const targetRow = rowRef.current[seq];
    if (container && targetRow) {
      container.scrollTo({
        top: targetRow.offsetTop - 700,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (projectData?.step === "검사") return;
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedRow, isKeyHandling, projectData?.step]);

  useEffect(() => {
    if (projectImagesInfo.length === 0) return;
    if (projectImagesInfo.length > 100) return;

    const validRows = projectImagesInfo.filter((row) => row.label === "");

    let targetRow: any;

    // label="" 이 있는 경우 -> 그 중 가장 seq 낮은 값(앞쪽에 있는 것)
    if (validRows.length > 0) {
      targetRow = validRows[0];
    } else {
      // label="" 없는 경우
      // - 최초 mount 시: index 0 선택
      // - 이후에는 자동 이동 X (return)
      if (!didInitialSelect) {
        targetRow = projectImagesInfo[0];
        setDidInitialSelect(true);
      } else {
        return; // 라벨링 전체 완료시 자동스크롤(0으로 돌아가기) X
      }
    }

    if (!targetRow || targetRow.seq === undefined) return;

    setSelectedRow(targetRow);

    setTimeout(() => {
      scrollToRow(targetRow.seq!);
    }, 0);
  }, [projectImagesInfo]);

  const columns: GridColDef[] = [
    { field: "no", headerName: t("No"), width: 40 },
    {
      field: "imageName",
      headerName: t("FileName"),
      width: 200,
      renderCell: (params: any) => (
        <Tooltip title={params.row.path}>
          <span>{params.row.imageName}</span>
        </Tooltip>
      ),
    },
    { field: "label", headerName: t("User"), width: 125 },
    { field: "AILabel", headerName: t("AI"), width: 125 },
    {
      field: "score",
      headerName: t("Score"),
      width: 150,
      renderCell: (params: any) => (
        <Tooltip title={params.row.path}>
          <span>{formatNumber(Number(params.row.score), 4)}</span>
        </Tooltip>
      ),
    },
  ];

  const fetchFileListByLabel = async (step: string, labels?: string[]) => {
    if (step === "검사") {
      const dsInfo = await getDatasetInfo(dataSetId);
      const dsFileInfo = await getFile(dsInfo.data.path);
      const pathList = extractImgPaths(dsFileInfo.data) as FilePathList;
      return { dsFilePath: dsInfo.data.path, pathList: pathList };
    } else {
      const dsInfo = await getDatasetInfo(dataSetId);
      const dsFileInfo = await getFile(dsInfo.data.path);
      const pathList = randomizeLabels(dsFileInfo.data as FilePathList, labels, projectConfig.trainTargetLabel);
      return { dsFilePath: dsInfo.data.path, pathList: pathList };
    }
  };

  const handleAnalysisProject = async () => {
    await updateDatasetFile(Number(id));
    const dsInfo = await getDatasetInfo(dataSetId);
    const dsFileInfo = await getFile(dsInfo.data.path);
    const pathList = extractImgPaths(dsFileInfo.data) as FilePathList;
    if (pathList["NG"] && pathList["NG"].length > 9) {
      setOpenAnalysisModal(true);
    } else {
      toast.warn(t("MinNGImageRequiredMsg"));
    }
  };

  const handleSaveAnalysis = async () => {
    if (projectData) {
      const classList =
        labelCount > 0
          ? Array.from({ length: labelCount }, (_, i) => String.fromCharCode(65 + i))
          : ["A", "B", "C", "D"];
      // await updateDatasetFile(Number(id));
      const response = await fetchFileListByLabel("분석", classList);
      const pathList = response.pathList;

      const newAnalysisProject: IAddProject = {
        config: {
          ...projectConfig,
          keyConfig:
            labelCount > 0 ? Array.from({ length: labelCount }, (_, i) => (i + 1).toString()) : ["1", "2", "3", "4"],
          trainTargetLabel: classList[0],
        },
        create_at: new Date(),
        dataSet_id: null,
        file_path: null,
        files: pathList,
        class_list:
          labelCount > 0
            ? Array.from({ length: labelCount }, (_, i) => String.fromCharCode(65 + i))
            : ["A", "B", "C", "D"],
        material_name: projectData.material_name,
        project_name: projectData.project_name + "_분석용도",
        step: "분석",
        train_id: null,
        folder_id: projectData.folder_id ? projectData.folder_id : null,
      };
      console.log(newAnalysisProject);
      const newAnalysisRes = await createProject(newAnalysisProject);
      navigate(`/main/project/${newAnalysisRes.project_id}/analysis`);
    }
  };

  const handleInspProject = async () => {
    const confirmed = run || window.confirm(t("CreateInspProjectConfirmMsg"));

    if (confirmed && projectData) {
      await updateDatasetFile(Number(id));
      const response = await fetchFileListByLabel("검사");
      const originPathList = response.pathList;
      const newPathList: Record<string, string[]> = {};

      Object.keys(originPathList).forEach((key) => {
        const value = originPathList[key as keyof typeof originPathList];

        if (Array.isArray(value)) {
          newPathList[key] = [...value];
        }
      });

      if ((newPathList["NG"]?.length ?? 0) < 10 || (newPathList["OK"]?.length ?? 0) < 10) {
        toast.warn(t("MinNGImageRequiredMsg"));
        return;
      }

      projectImagesInfo.forEach((item) => {
        const { label, AILabel, path } = item;

        if (!label && AILabel) {
          // 해당 key 없으면 생성
          if (!newPathList[AILabel]) {
            newPathList[AILabel] = [];
          }

          newPathList[AILabel].push(path);
        }
      });

      if (step == "분석" && label) {
        const formattedPathList: Record<string, string[]> = {
          OK: newPathList[label] ?? [],
          NG: Object.entries(newPathList)
            .filter(([key]) => key !== label && key !== "unlabeled")
            .flatMap(([, paths]) => paths),
          ...(newPathList["unlabeled"] ? { unlabeled: newPathList["unlabeled"] } : {}),
        };

        console.log(formattedPathList);
        // newPathList 재선언
        Object.keys(newPathList).forEach((key) => delete newPathList[key]);
        Object.assign(newPathList, formattedPathList);
      }

      const baseProjectName = `${projectData.project_name}_검사용도`;
      const uniqueProjectName = await getUniqueProjectName(baseProjectName);
      console.log("uniqueProjectName", uniqueProjectName);
      const newInspProject: IAddProject = {
        config: { ...initialProjectData.config, autoLabeling: false },
        create_at: new Date(),
        dataSet_id: null,
        file_path: null,
        files: newPathList,
        material_name: projectData.material_name,
        project_name: uniqueProjectName,
        step: "검사",
        train_id: null,
        inspModel_id: projectData.inspModel_id,
        folder_id: projectData.folder_id ? projectData.folder_id : null,
      };
      const newInspRes = await createProject(newInspProject);
      resetAIModule();
      navigate(`/main/project/${newInspRes.project_id}`);
    }
  };

  const handleNextClass = () => {
    if (!projectData?.class || !label) return;

    const currentIndex = projectData.class.indexOf(label);

    if (currentIndex === -1 || currentIndex === projectData.class.length - 1) {
      navigate(`/main/project/${id}/analysis`);
      return;
    }

    const nextLabel = run
      ? projectData.class[projectData.class.length - 1] // 마지막 라벨
      : projectData.class[currentIndex + 1];

    navigate(`/main/project/${id}/analysis/${nextLabel}`);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (isKeyHandling || e.key === "Process") return;

    const pressedKey = e.key.toLowerCase();
    const currentIndex = projectImagesInfo.findIndex((row) => row.seq === selectedRow.seq);

    if (pressedKey === "arrowdown") {
      e.preventDefault();
      const nextIndex = currentIndex + 1;
      if (projectImagesInfo[nextIndex]) {
        setSelectedRow(projectImagesInfo[nextIndex]);
      } else {
        toast.warning(t("LastImageMsg"));
      }
    } else if (pressedKey === "arrowup") {
      e.preventDefault();
      const prevIndex = currentIndex - 1;
      if (projectImagesInfo[prevIndex]) {
        setSelectedRow(projectImagesInfo[prevIndex]);
      } else {
        toast.warning(t("FirstImageMsg"));
      }
    }
    setTimeout(() => {
      setIsKeyHandling(false);
    }, 100);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      borderRight="1px solid"
      borderColor="var(--stroke)"
    >
      {step !== "검사" ? <ImageTable dataSetId={dataSetId} /> : <InspProjectTable dataSetId={dataSetId} />}
      <div className="flex items-center justify-between px-2 border-t border-gray-300 h-40px">
        <Tooltip title={t("RecentInspectionTime")} placement="top">
          <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-600">
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            {recentInspTime ? formatDateTime(recentInspTime) : "-"}
          </span>
        </Tooltip>
        {projectData?.step === "분류" && (
          <Button id="insp-move-btn" onClick={handleInspProject}>
            {t("MoveToInspStage")}
          </Button>
        )}
        {projectData?.step === "분석" && (
          <Button id="next-class-btn" onClick={handleNextClass}>
            {isLastClass ? t("ExitToMainMsg") : t("NextClassMsg")}
          </Button>
        )}
      </div>
      <Modal open={openAnalysisModal} onClose={() => setOpenAnalysisModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 380,
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 6,
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("SetClassCountTitle")}
          </Typography>

          {/* <TextField
            label="클래스 개수 (0~9)"
            value={labelCount}
            inputProps={{ maxLength: 1 }}
            onKeyDown={(e) => {
              // modal에서 Enter 등의 이벤트 빠져나가지 않도록
              e.stopPropagation();
            }}
            onChange={(e) => {
              e.preventDefault();
              const value = e.target.value;
              const last = value.slice(-1);
              if (!/^[0-9]$/.test(last)) return;
              setLabelCount(Number(last));
            }}
            fullWidth
          /> */}

          <TextField
            value={labelCount}
            label={t("ClassCountLabel")}
            inputProps={{ max: 9 }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              e.preventDefault();
              const value = e.target.value;
              const last = value.slice(-1);
              if (!/^[0-9]$/.test(last)) return;
              setLabelCount(Number(last));
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined">{t("Cancel")}</Button>
            <Button variant="contained" onClick={handleSaveAnalysis}>
              {t("Create")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
